import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import enLanding from './locales/en/landing.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enSettings from './locales/en/settings.json';
import enPricing from './locales/en/pricing.json';
import enSupport from './locales/en/support.json';

/**
 * Adding a new language (clean path):
 *   1. Create `frontend/src/i18n/locales/{code}/` with the 7 namespace JSON files.
 *   2. Add an entry to `SUPPORTED_LANGUAGES` below — the key is the BCP-47 tag,
 *      the value is the language's native name.
 *   3. Add the code to the grouped dropdown in `components/LanguageSwitcher.tsx`.
 *   4. (Optional) Add an alias mapping in `resolveLanguage()` below if you
 *      want browser variants (e.g. "es-AR") to fall through to a base locale.
 */
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  es: 'Espa\u00F1ol (US)',
  'es-ES': 'Espa\u00F1ol (ES)',
  'pt-BR': 'Portugu\u00EAs (BR)',
  'pt-PT': 'Portugu\u00EAs (PT)',
  'zh-CN': '\u7B80\u4F53\u4E2D\u6587',
  'zh-TW': '\u7E41\u9AD4\u4E2D\u6587',
  ja: '\u65E5\u672C\u8A9E',
  ko: '\uD55C\uAD6D\uC5B4',
  fr: 'Fran\u00E7ais',
  de: 'Deutsch',
  ru: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439',
  tr: 'T\u00FCrk\u00E7e',
  hi: '\u0939\u093F\u0928\u094D\u0926\u0940',
  id: 'Bahasa Indonesia',
  vi: 'Ti\u1EBFng Vi\u1EC7t',
  th: '\u0E44\u0E17\u0E22',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  uk: '\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430',
  cs: '\u010Ce\u0161tina',
  ro: 'Rom\u00E2n\u0103',
  hu: 'Magyar',
  sv: 'Svenska',
  da: 'Dansk',
  nb: 'Norsk',
  fi: 'Suomi',
  el: '\u0395\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC',
  ms: 'Bahasa Melayu',
  tl: 'Filipino',
  bn: '\u09AC\u09BE\u0982\u09B2\u09BE',
  ca: 'Catal\u00E0',
  hr: 'Hrvatski',
  bg: '\u0411\u044A\u043B\u0433\u0430\u0440\u0441\u043A\u0438',
  sr: '\u0421\u0440\u043F\u0441\u043A\u0438',
  sk: 'Sloven\u010Dina',
  sl: 'Sloven\u0161\u010Dina',
  et: 'Eesti',
  lv: 'Latvie\u0161u',
  lt: 'Lietuvi\u0173',
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

const ALL_NS = ['common', 'landing', 'auth', 'dashboard', 'settings', 'pricing', 'support'] as const;

/** Resolve a raw browser language tag to one of our supported language codes. */
export function resolveLanguage(raw: string): SupportedLanguage {
  if (raw in SUPPORTED_LANGUAGES) return raw as SupportedLanguage;
  // Portuguese: pt-PT → pt-PT, pt-BR or other pt-* → pt-BR
  if (raw === 'pt-PT' || raw === 'pt') return 'pt-PT';
  if (raw.startsWith('pt')) return 'pt-BR';
  // Spanish: es-ES → es-ES, es-US/es-419/other → es
  if (raw === 'es-ES') return 'es-ES';
  if (raw.startsWith('es')) return 'es';
  // zh-TW / zh-Hant → zh-TW, other zh-* → zh-CN
  if (raw.startsWith('zh-TW') || raw.startsWith('zh-Hant')) return 'zh-TW';
  if (raw.startsWith('zh')) return 'zh-CN';
  // Try base language (e.g. "de-AT" → "de")
  const base = raw.split('-')[0];
  if (base in SUPPORTED_LANGUAGES) return base as SupportedLanguage;
  return 'en';
}

export async function loadLanguage(lng: string) {
  if (lng === 'en') {
    await i18n.changeLanguage(lng);
    return;
  }
  await Promise.all(
    ALL_NS.map(async (ns) => {
      if (i18n.hasResourceBundle(lng, ns)) return;
      const res = await import(`./locales/${lng}/${ns}.json`);
      i18n.addResourceBundle(lng, ns, res.default, true, true);
    })
  );
  await i18n.changeLanguage(lng);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        landing: enLanding,
        auth: enAuth,
        dashboard: enDashboard,
        settings: enSettings,
        pricing: enPricing,
        support: enSupport,
      },
    },
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [...ALL_NS],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

/**
 * Resolves once the detected language bundles are loaded.
 * Awaited in main.tsx before rendering the app so pages never flash English.
 */
export const i18nReady: Promise<void> = (() => {
  const resolved = resolveLanguage(i18n.language ?? 'en');
  if (resolved === 'en') return Promise.resolve();
  return loadLanguage(resolved);
})();

export default i18n;
